import { useCallback } from 'react'
import { apiClient } from '../api/client'
import { API_ENDPOINTS } from '../api/endpoints'
import { useAuthStore } from '../store/authStore'
import { useLocation } from 'react-router-dom'

export type ActivityType = 
  | 'CREATE' 
  | 'UPDATE' 
  | 'DELETE' 
  | 'VIEW' 
  | 'PRINT' 
  | 'EXPORT' 
  | 'COPY' 
  | 'PASTE' 
  | 'LOGIN' 
  | 'LOGOUT' 
  | 'OPEN_PAGE'
  | 'SEARCH'
  | 'FILTER'
  | 'DOWNLOAD'
  | 'UPLOAD'
  | 'APPROVE'
  | 'REJECT'
  | 'CANCEL'
  | 'UNPOST'
  | 'GENERATE'
  | 'GENERATE_ALL'
  | 'BULK_UPDATE'
  | 'SAVE'
  | 'REVERSE'
  | 'SCAN'
  | 'TRANSFER'
  | 'RESTORE'
  | 'BACKUP'

export interface LogActivityParams {
  activityType: ActivityType
  entityType?: string
  entityId?: string
  actionDescription?: string
  oldValues?: any
  newValues?: any
  isSuccess?: boolean
  errorMessage?: string
  additionalData?: any
}

/**
 * Hook for logging user activities
 * Automatically tracks page views and allows manual logging of actions
 */
export function useActivityLogger() {
  const { user } = useAuthStore()
  const location = useLocation()

  // Generate session ID (persist in sessionStorage)
  const getSessionId = useCallback(() => {
    let sessionId = sessionStorage.getItem('activitySessionId')
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      sessionStorage.setItem('activitySessionId', sessionId)
    }
    return sessionId
  }, [])

  /**
   * Log an activity to the server
   */
  const logActivity = useCallback(async (params: LogActivityParams) => {
    try {
      // Don't log if user is not authenticated
      if (!user) {
        return
      }

      const activityData = {
        userId: user.id ? parseInt(user.id) : null,
        userName: user.username || user.email,
        companyId: user.companyId ? parseInt(user.companyId) : null,
        branchId: user.branchId || user.branchCode,
        activityType: params.activityType,
        entityType: params.entityType,
        entityId: params.entityId,
        pagePath: location.pathname,
        actionDescription: params.actionDescription,
        oldValues: params.oldValues ? JSON.stringify(params.oldValues) : null,
        newValues: params.newValues ? JSON.stringify(params.newValues) : null,
        sessionId: getSessionId(),
        isSuccess: params.isSuccess ?? true,
        errorMessage: params.errorMessage,
        additionalData: params.additionalData ? JSON.stringify(params.additionalData) : null,
      }

      // Fire and forget - don't block UI if logging fails
      apiClient.post(API_ENDPOINTS.activityLogs.create, activityData).catch((error) => {
        // Silently fail - logging shouldn't break the app
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to log activity:', error)
        }
      })
    } catch (error) {
      // Silently fail - logging shouldn't break the app
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error logging activity:', error)
      }
    }
  }, [user, location.pathname, getSessionId])

  /**
   * Log a page view
   */
  const logPageView = useCallback(() => {
    logActivity({
      activityType: 'OPEN_PAGE',
      actionDescription: `Opened page: ${location.pathname}`,
    })
  }, [logActivity, location.pathname])

  /**
   * Log a create action
   */
  const logCreate = useCallback((entityType: string, entityId: string, newValues: any, description?: string) => {
    logActivity({
      activityType: 'CREATE',
      entityType,
      entityId,
      newValues,
      actionDescription: description || `Created ${entityType}: ${entityId}`,
    })
  }, [logActivity])

  /**
   * Log an update action
   */
  const logUpdate = useCallback((entityType: string, entityId: string, oldValues: any, newValues: any, description?: string) => {
    logActivity({
      activityType: 'UPDATE',
      entityType,
      entityId,
      oldValues,
      newValues,
      actionDescription: description || `Updated ${entityType}: ${entityId}`,
    })
  }, [logActivity])

  /**
   * Log a delete action
   */
  const logDelete = useCallback((entityType: string, entityId: string, description?: string) => {
    logActivity({
      activityType: 'DELETE',
      entityType,
      entityId,
      actionDescription: description || `Deleted ${entityType}: ${entityId}`,
    })
  }, [logActivity])

  /**
   * Log a view action
   */
  const logView = useCallback((entityType: string, entityId: string, description?: string) => {
    logActivity({
      activityType: 'VIEW',
      entityType,
      entityId,
      actionDescription: description || `Viewed ${entityType}: ${entityId}`,
    })
  }, [logActivity])

  /**
   * Log a print action
   */
  const logPrint = useCallback((entityType: string, entityId: string, description?: string) => {
    logActivity({
      activityType: 'PRINT',
      entityType,
      entityId,
      actionDescription: description || `Printed ${entityType}: ${entityId}`,
    })
  }, [logActivity])

  /**
   * Log an export action
   */
  const logExport = useCallback((entityType: string, format?: string, description?: string) => {
    logActivity({
      activityType: 'EXPORT',
      entityType,
      actionDescription: description || `Exported ${entityType}${format ? ` as ${format}` : ''}`,
      additionalData: { format },
    })
  }, [logActivity])

  /**
   * Log a copy action
   */
  const logCopy = useCallback((entityType: string, entityId: string, description?: string) => {
    logActivity({
      activityType: 'COPY',
      entityType,
      entityId,
      actionDescription: description || `Copied ${entityType}: ${entityId}`,
    })
  }, [logActivity])

  /**
   * Log a paste action
   */
  const logPaste = useCallback((entityType: string, description?: string) => {
    logActivity({
      activityType: 'PASTE',
      entityType,
      actionDescription: description || `Pasted ${entityType}`,
    })
  }, [logActivity])

  /**
   * Log a search action
   */
  const logSearch = useCallback((entityType: string, searchTerm: string, description?: string) => {
    logActivity({
      activityType: 'SEARCH',
      entityType,
      actionDescription: description || `Searched ${entityType}: ${searchTerm}`,
      additionalData: { searchTerm },
    })
  }, [logActivity])

  /**
   * Log an error
   */
  const logError = useCallback((activityType: ActivityType, entityType: string, errorMessage: string, additionalData?: any) => {
    logActivity({
      activityType,
      entityType,
      isSuccess: false,
      errorMessage,
      additionalData,
    })
  }, [logActivity])

  return {
    logActivity,
    logPageView,
    logCreate,
    logUpdate,
    logDelete,
    logView,
    logPrint,
    logExport,
    logCopy,
    logPaste,
    logSearch,
    logError,
  }
}

